import {
  IsArray,
  IsBoolean,
  IsEnum,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
} from 'class-validator';
import {
  MICRONUTRIENT_CODES,
  type MicronutrientCode,
} from '../../nutrition/micronutrient-registry';
import {
  MICRONUTRIENT_SOURCE_TYPES,
  type MicronutrientSourceType,
} from '../ingredient/micronutrient-source-type.enum';

export const RECIPE_NUTRITION_BASES = ['FINISHED', 'RAW'] as const;
export type RecipeNutritionBasis = (typeof RECIPE_NUTRITION_BASES)[number];

export const RECIPE_NUTRITION_ISSUE_CODES = [
  'UNLINKED_INGREDIENT',
  'MISSING_NUTRITIONAL_PROFILE',
  'MISSING_DENSITY',
  'MISSING_PIECE_WEIGHT',
  'UNSUPPORTED_MEASUREMENT_UNIT',
  'MISSING_EDIBLE_PORTION_FACTOR',
  'OPTIONAL_ENERGY_COMPONENTS_ASSUMED_ZERO',
  'OPTIONAL_FIBER_NOT_DECLARED',
  'DECLARED_SALT_FALLBACK_NO_SODIUM',
  'RAW_WEIGHT_FALLBACK',
  /** Declared finished weight far outside a plausible cooking yield of the ingredients. */
  'IMPLAUSIBLE_FINISHED_WEIGHT',
  'MISSING_SERVINGS',
  'SUB_RECIPE_CYCLE',
  'SUB_RECIPE_WEIGHT_UNRESOLVED',
  'ZERO_DENOMINATOR',
  'MICRONUTRIENT_COVERAGE_INCOMPLETE',
  'MICRONUTRIENT_PROVENANCE_MISSING',
  /** A COUNT sub-recipe reference without an explicit weight was interpreted as N whole child batches. */
  'SUB_RECIPE_COUNT_AS_BATCHES',
  /** Scaling changed the produced weight but the servings count could not be scaled to a sensible whole number. */
  'SERVINGS_SCALE_NOT_REPRESENTABLE',
] as const;
export type RecipeNutritionIssueCode = (typeof RECIPE_NUTRITION_ISSUE_CODES)[number];

export const RECIPE_NUTRITION_ISSUE_SEVERITIES = ['INFO', 'WARNING', 'ERROR'] as const;
export type RecipeNutritionIssueSeverity = (typeof RECIPE_NUTRITION_ISSUE_SEVERITIES)[number];

/**
 * Transport-precision calculation values. They are deliberately not legal
 * presentation values: UI/PDF/export must pass them through
 * `formatEuNutritionValue`, the single EU rounding/banding policy.
 */
export class RecipeNutritionalValuesDto {
  @IsNumber()
  energyKj!: number;

  @IsNumber()
  energyKcal!: number;

  @IsNumber()
  fat!: number;

  @IsNumber()
  saturatedFat!: number;

  @IsNumber()
  carbohydrate!: number;

  @IsNumber()
  sugars!: number;

  @IsNumber()
  fiber!: number;

  @IsNumber()
  protein!: number;

  @IsNumber()
  salt!: number;
}

export class RecipeNutritionIssueDto {
  @IsEnum(RECIPE_NUTRITION_ISSUE_CODES)
  code!: RecipeNutritionIssueCode;

  @IsEnum(RECIPE_NUTRITION_ISSUE_SEVERITIES)
  severity!: RecipeNutritionIssueSeverity;

  @IsString()
  message!: string;

  @IsOptional()
  @IsUUID('loose')
  recipeId?: string;

  @IsOptional()
  @IsUUID('loose')
  recipeIngredientId?: string;

  @IsOptional()
  @IsUUID('loose')
  ingredientId?: string;

  /**
   * Populated only for MICRONUTRIENT_COVERAGE_INCOMPLETE: the issue is
   * recipe-wide (one vitamin can be undeclared across several lines), so it
   * cannot reuse the single `recipeIngredientId` above.
   */
  @IsOptional()
  @IsEnum(MICRONUTRIENT_CODES)
  nutrientCode?: MicronutrientCode;

  @IsOptional()
  @IsArray()
  missingLineKeys?: string[];
}

export class RecipeNutritionIngredientBreakdownDto {
  @IsUUID('loose')
  recipeId!: string;

  @IsOptional()
  @IsUUID('loose')
  recipeIngredientId?: string;

  @IsOptional()
  @IsUUID('loose')
  ingredientId?: string;

  @IsString()
  name!: string;

  @IsNumber()
  grossWeightG!: number;

  @IsNumber()
  edibleWeightG!: number;

  @IsBoolean()
  resolved!: boolean;

  @IsOptional()
  values?: RecipeNutritionalValuesDto;
}

/**
 * One vitamin from the Annex XIII Part A point 1 registry, present only when
 * at least one recipe line declared a value for it (an undeclared vitamin
 * across every line is omitted from the array entirely, never emitted as
 * zero — see `RecipeMicronutrientTotalsDto`/`addMicronutrientValues` in the
 * engine for the "absent means unknown, not zero" contribution rule).
 */
export class RecipeMicronutrientDto {
  @IsEnum(MICRONUTRIENT_CODES)
  nutrientCode!: MicronutrientCode;

  @IsEnum(['MG', 'MCG'])
  unit!: 'MG' | 'MCG';

  /** FORTIFICATION when any contributing ingredient adds this vitamin. */
  @IsEnum(MICRONUTRIENT_SOURCE_TYPES)
  sourceType!: MicronutrientSourceType;

  @IsNumber()
  totalPer100g!: number;

  @IsOptional()
  @IsNumber()
  perPortion!: number | null;

  @IsNumber()
  percentNrvAdultsPer100!: number;

  @IsBoolean()
  significant!: boolean;

  /** True only if every contributing line declared this vitamin. */
  @IsBoolean()
  complete!: boolean;

  /**
   * True when every fortified contribution is backed by a declared-label
   * value or an explicit end-of-shelf-life guarantee. Natural vitamins do not
   * require this additional declaration.
   */
  @IsBoolean()
  endOfShelfLifeReady!: boolean;
}

/**
 * Reproducible result of the current recipe calculation. `basis=RAW` is an
 * explicit legal/product-data warning: the recipe has no declared finished
 * weight, so per-100g values use the sum of resolved raw weights.
 */
export class RecipeNutritionResponseDto {
  @IsUUID('loose')
  recipeId!: string;

  @IsEnum(RECIPE_NUTRITION_BASES)
  basis!: RecipeNutritionBasis;

  @IsNumber()
  denominatorWeightG!: number;

  @IsNumber()
  rawWeightG!: number;

  @IsOptional()
  @IsNumber()
  finishedWeightG!: number | null;

  @IsOptional()
  @IsNumber()
  servings!: number | null;

  @IsBoolean()
  complete!: boolean;

  total!: RecipeNutritionalValuesDto;

  per100g!: RecipeNutritionalValuesDto;

  @IsOptional()
  perPortion!: RecipeNutritionalValuesDto | null;

  @IsArray()
  ingredients!: RecipeNutritionIngredientBreakdownDto[];

  @IsArray()
  micronutrients!: RecipeMicronutrientDto[];

  @IsArray()
  issues!: RecipeNutritionIssueDto[];
}
